import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { Checkbox, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { sortProgressCheckpoints } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import type { PlayerProgressItem } from '@/features/player/components/types';
import {
  formatProgressBadgeTitle,
  formatProgressCheckpointTitleOverview,
} from '@/features/player/utils/progress-title';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import {
  badgeAggregateStatusFromCheckpoints,
  checkpointDisplayStatus,
  type LevelStatusKey,
} from './mutate-level-status';

export type { LevelStatusKey };

export type LevelRowProps = {
  label: string;
  statusKey: LevelStatusKey;
  levelColor: string;
  current: boolean;
  editMode: boolean;
  canCheck: boolean;
  checked: boolean;
  onToggle: () => void;
  editBgDisabled: boolean;
  onPressCard: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  progressItem?: PlayerProgressItem;
  expandedBadges: Set<string>;
  onToggleBadgeExpanded: (badgeKey: string) => void;
  selectedBadgeKeys: Set<string>;
  onToggleBadgeChecked: (badgeKey: string, nextChecked: boolean) => void;
  selectedCheckpointKeys: Set<string>;
  onToggleCheckpointChecked: (checkpointKey: string, nextChecked: boolean) => void;
  // styles are owned by the parent screen to avoid duplication
  styles: Record<string, any>;
};

export function LevelRow({
  label,
  statusKey,
  levelColor,
  current,
  editMode,
  canCheck,
  checked,
  onToggle,
  onPressCard,
  expanded,
  onToggleExpanded,
  progressItem,
  expandedBadges,
  onToggleBadgeExpanded,
  selectedBadgeKeys,
  onToggleBadgeChecked,
  selectedCheckpointKeys,
  onToggleCheckpointChecked,
  styles,
}: LevelRowProps) {
  const { scale } = useScaling();

  const levelStatusLabel =
    statusKey === 'completed'
      ? STRINGS.player.mutatePlayer.levelTab.statusCompleted
      : statusKey === 'partial'
        ? STRINGS.player.mutatePlayer.levelTab.statusPartial
        : STRINGS.player.mutatePlayer.levelTab.statusIncomplete;

  const levelInlineStatusStyle = [
    styles.levelRowStatusText,
    statusKey === 'completed'
      ? styles.levelRowStatusCompleted
      : statusKey === 'partial'
        ? styles.levelRowStatusPartial
        : styles.levelRowStatusIncomplete,
  ];

  const editBgStyle = styles.levelRowEditBgEnabled;

  const badges = React.useMemo(() => progressItem?.badges ?? [], [progressItem?.badges]);

  const badgeStatusLabel = (key: LevelStatusKey) =>
    key === 'completed'
      ? STRINGS.player.mutatePlayer.levelTab.statusCompleted
      : key === 'partial'
        ? STRINGS.player.mutatePlayer.levelTab.statusPartial
        : STRINGS.player.mutatePlayer.levelTab.statusIncomplete;

  const badgeInlineStyle = (key: LevelStatusKey) => [
    styles.levelRowStatusText,
    key === 'completed'
      ? styles.levelRowStatusCompleted
      : key === 'partial'
        ? styles.levelRowStatusPartial
        : styles.levelRowStatusIncomplete,
  ];

  const card = (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={() => {
        if (editMode) return;
        onPressCard();
      }}
      disabled={editMode}
      style={[
        styles.levelRow,
        editMode ? [styles.levelRowEdit, editBgStyle] : styles.levelRowView,
      ]}>
      <Stack width="100%" style={styles.levelRowInner}>
        <Stack direction="row" align="center" justify="space-between" width="100%">
          <Stack flex={1} minWidth={0} paddingRight={scale(8)}>
            <Text
              variant="Font Inter/Subtitle/Bold 14px"
              style={[styles.levelTitle, { color: levelColor }]}>
              {label}
            </Text>
          </Stack>
          <Stack direction="row" align="center" gap={scale(8)}>
            <Text
              fontSize={scale(10)}
              fontWeight="600"
              fontFamily="Montserrat"
              style={levelInlineStatusStyle}>
              {levelStatusLabel}
            </Text>
            <TouchableOpacity
              hitSlop={scale(10)}
              onPress={onToggleExpanded}
              activeOpacity={0.7}
              style={styles.chevronButton}>
              <SvgIcon
                name="IcPaginationArrow"
                size={scale(18)}
                color={colors.Blue}
                style={[styles.chevronIcon, expanded && styles.chevronIconExpanded]}
              />
            </TouchableOpacity>
          </Stack>
        </Stack>

        {expanded ? (
          <Stack style={styles.levelExpanded}>
            {(badges ?? []).map((badge, idx) => {
              const badgeTitle = formatProgressBadgeTitle(badge.name);
              const badgeKey = `${label}-${badge.name}-${idx}`;
              const isBadgeExpanded = expandedBadges.has(badgeKey);
              const isBadgeChecked = selectedBadgeKeys.has(badgeKey);
              const checkpoints = sortProgressCheckpoints(badge.checkpoints);
              const badgeAgg = badgeAggregateStatusFromCheckpoints(badge);
              return (
                <Stack key={badgeKey} style={styles.badgeBlock}>
                  <Stack
                    direction="row"
                    align="center"
                    justify="space-between"
                    width={'100%'}
                    style={[
                      styles.badgeRow,
                      idx === badges.length - 1 && { borderBottomColor: colors['0'] },
                      isBadgeExpanded ? styles.badgeRowExpanded : null,
                    ]}>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => onToggleBadgeExpanded(badgeKey)}
                      style={styles.badgeRowLeft}>
                      <SvgIcon
                        name="IcPaginationArrow"
                        size={scale(16)}
                        color={colors.Blue}
                        style={[
                          styles.badgeChevron,
                          isBadgeExpanded && styles.badgeChevronExpanded,
                        ]}
                      />
                      <Text
                        variant="Font Inter/Subtitle/Bold 12px"
                        style={[styles.badgeTitle, { color: levelColor }]}
                        numberOfLines={1}>
                        {badgeTitle}
                      </Text>
                    </TouchableOpacity>

                    {editMode && badgeAgg !== 'incomplete' ? (
                      <TouchableOpacity
                        activeOpacity={0.7}
                        hitSlop={scale(10)}
                        onPress={() => onToggleBadgeChecked(badgeKey, !isBadgeChecked)}>
                        <Checkbox
                          checked={isBadgeChecked}
                          onValueChange={next => onToggleBadgeChecked(badgeKey, next)}
                          style={styles.checkboxFaded}
                        />
                      </TouchableOpacity>
                    ) : (
                      <Text
                        fontSize={scale(10)}
                        fontWeight="600"
                        fontFamily="Montserrat"
                        style={badgeInlineStyle(badgeAgg)}>
                        {badgeStatusLabel(badgeAgg)}
                      </Text>
                    )}
                  </Stack>

                  {isBadgeExpanded ? (
                    <Stack style={styles.checkpointsBlock}>
                      {checkpoints.map(cp => {
                        const checkpointKey = `${badgeKey}-${String(cp.name ?? '').trim()}`;
                        const isCheckpointChecked = selectedCheckpointKeys.has(checkpointKey);
                        const cpDisp = checkpointDisplayStatus(cp.status);
                        const cpLabel =
                          cpDisp === 'completed'
                            ? STRINGS.player.mutatePlayer.levelTab.statusCompleted
                            : STRINGS.player.mutatePlayer.levelTab.statusIncomplete;
                        const cpStyle = [
                          styles.levelRowStatusText,
                          cpDisp === 'completed'
                            ? styles.levelRowStatusCompleted
                            : styles.levelRowStatusIncomplete,
                        ];
                        return (
                          <Stack
                            key={checkpointKey}
                            direction="row"
                            align="center"
                            justify="space-between"
                            style={[styles.checkpointRow, styles.checkpointRowDivider]}>
                            <Text
                              variant="Font Inter/Subtitle/Regular 12px"
                              style={styles.checkpointTitle}>
                              {formatProgressCheckpointTitleOverview(cp.name, {
                                layerSeparator: ' / ',
                              })}
                            </Text>
                            {editMode && cpDisp === 'completed' ? (
                              <TouchableOpacity
                                activeOpacity={0.7}
                                hitSlop={scale(10)}
                                onPress={() =>
                                  onToggleCheckpointChecked(checkpointKey, !isCheckpointChecked)
                                }>
                                <Checkbox
                                  checked={isCheckpointChecked}
                                  onValueChange={next =>
                                    onToggleCheckpointChecked(checkpointKey, next)
                                  }
                                  style={styles.checkboxFaded}
                                />
                              </TouchableOpacity>
                            ) : (
                              <Text
                                fontSize={scale(10)}
                                fontWeight="600"
                                fontFamily="Montserrat"
                                style={cpStyle}>
                                {cpLabel}
                              </Text>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        ) : null}
      </Stack>
      {current ? (
        <Stack style={styles.currentBadge}>
          <Text style={styles.currentBadgeText}>
            {STRINGS.player.mutatePlayer.levelTab.current}
          </Text>
        </Stack>
      ) : null}
    </TouchableOpacity>
  );

  if (!editMode) {
    return <Stack width="100%">{card}</Stack>;
  }

  return (
    <Stack direction="row" align="flex-start" gap={scale(12)} width="100%">
      <Stack width={scale(24)} align="center" justify="center" paddingTop={scale(22)}>
        {canCheck ? (
          <Checkbox checked={checked} onValueChange={onToggle} style={styles.checkboxFaded} />
        ) : null}
      </Stack>
      <Stack flex={1}>{card}</Stack>
    </Stack>
  );
}
